import { Search, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Input } from '@/components/ui/input';

// Map of searchable keywords to their corresponding settings pages
const SETTINGS_PAGE_KEYWORDS: Record<string, string[]> = {
  '/settings': ['overview', 'dashboard', 'summary', 'statistics', 'stats'],
  '/settings/profile': ['profile', 'my profile', 'personal', 'name', 'email', 'password', 'account'],
  '/settings/notifications': ['notifications', 'alerts', 'notify', 'notification'],
  '/settings/messages': ['messages', 'inbox', 'messaging', 'chat'],
  '/settings/invoices': ['invoices', 'billing', 'invoice', 'payment', 'payments'],
  '/settings/company': ['company', 'organization', 'logo', 'branding', 'industry'],
  '/settings/users': ['users', 'user', 'members', 'team', 'teams', 'invite', 'invitations', 'staff', 'roles', 'permissions', 'access'],
  '/settings/groups': ['groups', 'group', 'productions', 'production', 'shows', 'events', 'projects'],
  '/settings/units': ['units', 'unit', 'units', 'units', 'unit', 'trailers', 'vehicles', 'assets'],
  '/settings/audit': ['audit', 'logs', 'history', 'activity', 'changes'],
  '/settings/companies': ['companies', 'all companies', 'companies', 'organizations'],
  '/settings/global-audit': ['global audit', 'platform audit', 'system logs'],
  '/settings/filters': ['metadata', 'filters', 'labels', 'tags', 'categories'],
  '/settings/config': ['config', 'configuration', 'system config', 'settings', 'preferences'],
  '/settings/devices': ['devices', 'system devices', 'starlink', 'solark', 'sol-ark', 'gps', 'omni', 'tesla', 'hvac', 'hardware', 'terminals', 'inverters'],
};

// Find the best matching page for a search query
function findMatchingPage(query: string, currentPath: string): string | null {
  const searchLower = query.toLowerCase().trim();
  if (!searchLower) return null;

  let bestMatch: { path: string; score: number } | null = null;

  for (const [path, keywords] of Object.entries(SETTINGS_PAGE_KEYWORDS)) {
    // Skip current page
    if (path === currentPath) continue;

    for (const keyword of keywords) {
      if (keyword.includes(searchLower) || searchLower.includes(keyword)) {
        const score = keyword === searchLower ? 100 :
                     keyword.startsWith(searchLower) ? 80 :
                     searchLower.startsWith(keyword) ? 70 : 50;

        if (!bestMatch || score > bestMatch.score) {
          bestMatch = { path, score };
        }
      }
    }
  }

  return bestMatch?.path ?? null;
}

interface SettingsFilterBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  placeholder?: string;
  enableGlobalNavigation?: boolean;
}

/**
 * Simplified filter bar for Settings pages
 * Only provides search functionality - all other filters are in the main StatusBar
 * When enableGlobalNavigation is true, pressing Enter navigates to matching settings pages
 */
export function SettingsFilterBar({
  searchQuery,
  onSearchChange,
  placeholder = "Search...",
  enableGlobalNavigation = true,
}: SettingsFilterBarProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && enableGlobalNavigation && searchQuery.trim()) {
      const matchingPage = findMatchingPage(searchQuery, location.pathname);
      if (matchingPage) {
        navigate(matchingPage);
        onSearchChange(''); // Clear search after navigation
      }
    }
  };

  return (
    <div className="relative w-full sm:w-80">
      <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
      <Input
        placeholder={placeholder}
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        onKeyDown={handleKeyDown}
        className="pl-9 bg-background border-border"
      />
      {searchQuery && (
        <button
          onClick={() => onSearchChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center hover:bg-muted rounded transition-colors h-6 w-6"
        >
          <X className="h-4 w-4 text-muted-foreground" />
        </button>
      )}
    </div>
  );
}

